import { Link } from "react-router-dom";

/** Shared marketing-site shell: top-nav + footer used across all public pages
 *  (Home / About / Services / Contact / Privacy / Terms). The internal app
 *  (dashboard, projects, admin, etc.) is unaffected — that runs under
 *  AppLayout. This shell is UI-only and never touches auth, pipeline,
 *  agents, DB, routes, or env vars.  */
export function MarketingNav() {
    return (
        <header className="relative z-20 px-6 sm:px-10 py-5 flex items-center justify-between" data-testid="marketing-nav">
            <Link to="/" className="flex items-center gap-3" data-testid="marketing-brand">
                <div className="w-8 h-8 rounded-sm bg-[#FF4400] flex items-center justify-center font-bold text-white">F</div>
                <div>
                    <div className="font-display text-base leading-none text-white">Furmais</div>
                    <div className="label-micro mt-1">Creative OS</div>
                </div>
            </Link>
            <nav className="hidden sm:flex items-center gap-6 text-xs font-mono uppercase tracking-wider text-zinc-500">
                <Link to="/about" className="hover:text-white transition-colors" data-testid="nav-about">About</Link>
                <Link to="/services" className="hover:text-white transition-colors" data-testid="nav-services">Services</Link>
                <Link to="/contact" className="hover:text-white transition-colors" data-testid="nav-contact">Contact</Link>
            </nav>
            <Link
                to="/login"
                data-testid="nav-login"
                className="text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white"
            >
                Sign in →
            </Link>
        </header>
    );
}

export function MarketingFooter() {
    return (
        <footer className="relative z-10 border-t border-zinc-900 mt-24 px-6 sm:px-10 py-12" data-testid="marketing-footer">
            <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-sm bg-[#FF4400] flex items-center justify-center font-bold text-white text-sm">F</div>
                        <div className="font-display text-base text-white">Furmais</div>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed">
                        Your wish, powered by AI.
                    </p>
                </div>
                <FooterCol title="Product" links={[
                    { to: "/services", label: "Services" },
                    { to: "/about", label: "About" },
                    { to: "/contact", label: "Contact" },
                    { to: "/login", label: "Sign in" },
                ]} />
                <FooterCol title="Resources" links={[
                    { to: "/privacy", label: "Privacy Policy" },
                    { to: "/terms", label: "Terms of Service" },
                ]} />
                <div>
                    <div className="label-micro mb-3">Reach us</div>
                    <ul className="text-sm text-zinc-300 space-y-1.5">
                        <li><a href="mailto:support@furmais.com" className="hover:text-white" data-testid="footer-email">support@furmais.com</a></li>
                        <li><a href="tel:+918866065375" className="hover:text-white" data-testid="footer-phone">+91 88660 65375</a></li>
                        <li className="text-zinc-500 text-xs pt-1">Surat, Gujarat, India</li>
                    </ul>
                </div>
            </div>
            <div className="max-w-6xl mx-auto pt-10 mt-10 border-t border-zinc-900 text-[10px] font-mono uppercase tracking-wider text-zinc-700">
                © Furmais. Built for creative production teams.
            </div>
        </footer>
    );
}

function FooterCol({ title, links }) {
    return (
        <div>
            <div className="label-micro mb-3">{title}</div>
            <ul className="space-y-1.5">
                {links.map(l => (
                    <li key={l.to}>
                        <Link to={l.to} className="text-sm text-zinc-300 hover:text-white">{l.label}</Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** Wraps any marketing page with consistent nav, footer, dark canvas, and
 *  per-page SEO metadata via document.title / og tags. */
export default function MarketingShell({ children, title, description, ogTitle, ogDescription }) {
    if (typeof document !== "undefined") {
        if (title) document.title = title;
        if (description) {
            const meta = document.querySelector('meta[name="description"]');
            if (meta) meta.setAttribute("content", description);
        }
        // OG tags created on-demand for each page so the share preview stays specific.
        const setOg = (prop, value) => {
            if (!value) return;
            let el = document.querySelector(`meta[property="${prop}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("property", prop);
                document.head.appendChild(el);
            }
            el.setAttribute("content", value);
        };
        setOg("og:title", ogTitle || title);
        setOg("og:description", ogDescription || description);
        setOg("og:type", "website");
    }
    return (
        <div className="relative min-h-screen overflow-hidden bg-[#050505] text-white" data-testid="marketing-shell">
            <MarketingNav />
            {children}
            <MarketingFooter />
        </div>
    );
}
